/**
 * DraftReviewTabs — Sprint 4 right-pane component for full_draft mode.
 *
 * Two tabs:
 *   • "Draft" / "المسودة" — Scribe's document text. Default tab.
 *   • "Review" / "المراجعة" — Conductor's risk + compliance findings.
 *
 * The Review tab shows a badge with the finding count so the user
 * knows there's content there to check.
 *
 * Visual idiom: same cream/black palette as the rest of the app.
 * Uses ContractEditor for the Draft tab so all the existing reading-
 * pane affordances (citations, RTL, copy) remain intact.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ContractEditor from './ContractEditor';
import ReactMarkdown from 'react-markdown';

function countFindings(reviewText) {
  if (!reviewText) return 0;
  // Heuristic: count Markdown bullet lines or numbered sections under
  // "## Risk Findings" / "## Compliance Status". This is a UI hint,
  // not a strict count — purpose is to nudge the user to the Review
  // tab when there's content.
  const bullets = (reviewText.match(/^\s*[-*•]\s+/gm) || []).length;
  const numbered = (reviewText.match(/^\s*\d+[.)]\s+/gm) || []).length;
  return bullets + numbered;
}

export function DraftReviewTabs({
  draftText,
  reviewText,
  isArabic,
  contractType,
  citations,
  onAction,
  conductorCompleted = false,
  // P2 fix (Feb 2026): hide the Review tab on Bayenah-drafted matters.
  // Self-review of our own draft is a UX anti-pattern (cf. the
  // phantom-findings incident on the 26-article NDA) — Review is now
  // reserved for user-uploaded contracts only. Callers pass
  // `sourceKind` so this component is the single gate; the BE still
  // runs Examiner so the data exists if we ever want to re-enable.
  sourceKind = 'uploaded',
}) {
  const reviewVisible = sourceKind !== 'bayenah_drafted';
  const [activeTab, setActiveTab] = useState('draft');

  // Jul 2026 (live-typing flow): while the Scribe is still writing
  // (draft text growing, Conductor not yet complete), keep the draft
  // pane scrolled to the bottom so the user watches words being typed
  // — the standard AI-drafting experience. Stops the moment the run
  // completes so the user can read from wherever they like.
  const draftPaneRef = useRef(null);
  useEffect(() => {
    if (conductorCompleted || !draftText) return;
    const pane = draftPaneRef.current;
    if (!pane) return;
    const scroller = pane.querySelector('.overflow-y-auto');
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, [draftText, conductorCompleted]);
  // Sprint 4 Fix 3: the Review tab badge — and the pulse animation
  // that draws the user's eye to it — fires only AFTER Conductor
  // emits `conductor_complete`. Before that, the Review tab is
  // present but unstyled, so the user isn't lured to an empty pane
  // mid-streaming. The pulse triggers once on the transition from
  // not-completed → completed.
  const findingsCount = useMemo(
    () => (conductorCompleted ? countFindings(reviewText) : 0),
    [conductorCompleted, reviewText],
  );
  const prevCompletedRef = useRef(false);
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (conductorCompleted && !prevCompletedRef.current && findingsCount > 0) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 1800);
      prevCompletedRef.current = true;
      return () => clearTimeout(t);
    }
    if (conductorCompleted) prevCompletedRef.current = true;
    return undefined;
  }, [conductorCompleted, findingsCount]);

  const tabs = reviewVisible
    ? [
        {
          key: 'draft',
          label: isArabic ? 'المسودة' : 'Draft',
          badge: null,
        },
        {
          key: 'review',
          label: isArabic ? 'المراجعة' : 'Review',
          badge: findingsCount > 0 ? findingsCount : null,
        },
      ]
    : [
        {
          key: 'draft',
          label: isArabic ? 'المسودة' : 'Draft',
          badge: null,
        },
      ];

  // P2 fix: if Review is hidden but state still points there, render
  // Draft for this tick — no useEffect → no `set-state-in-effect`
  // anti-pattern, no extra render.
  const effectiveTab = (!reviewVisible && activeTab === 'review') ? 'draft' : activeTab;

  return (
    <div
      className="h-full w-full flex flex-col"
      data-testid="draft-review-tabs"
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {/* Tab strip */}
      <div
        className="flex border-b"
        style={{ borderColor: '#E8E8E8', flexDirection: isArabic ? 'row-reverse' : 'row' }}
      >
        {tabs.map((t) => {
          const active = effectiveTab === t.key;
          const isReview = t.key === 'review';
          const shouldPulse = isReview && pulse;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              data-testid={`draft-review-tab-${t.key}`}
              data-active={active}
              data-pulsing={shouldPulse ? 'true' : 'false'}
              className={`px-5 py-3 text-sm font-medium transition-colors ${shouldPulse ? 'sprint4-review-pulse' : ''}`}
              style={{
                color: active ? '#111' : '#777',
                borderBottom: active ? '2px solid #111' : '2px solid transparent',
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              <span>{t.label}</span>
              {t.badge !== null && (
                <span
                  data-testid={`draft-review-tab-${t.key}-badge`}
                  className="ml-2 inline-flex items-center justify-center"
                  style={{
                    minWidth: 20, height: 20, borderRadius: 9999,
                    background: '#111', color: '#fff',
                    fontSize: 11, fontWeight: 600, padding: '0 6px',
                    marginInlineStart: 8,
                  }}
                >
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* One-time pulse animation that fires when the Conductor's
          findings land — draws the user's eye to the Review tab.
          Scoped class so it cannot leak into other panes. */}
      <style>{`
        @keyframes sprint4ReviewPulse {
          0%   { background-color: rgba(17,17,17,0.00); }
          25%  { background-color: rgba(17,17,17,0.08); }
          60%  { background-color: rgba(17,17,17,0.04); }
          100% { background-color: rgba(17,17,17,0.00); }
        }
        .sprint4-review-pulse {
          animation: sprint4ReviewPulse 1.6s ease-out 1;
        }
      `}</style>

      {/* Tab body */}
      <div ref={draftPaneRef} className="flex-1 overflow-hidden" data-testid={`draft-review-pane-${effectiveTab}`}>
        {effectiveTab === 'draft' ? (
          <ContractEditor
            content={draftText || ''}
            onChange={() => { /* read-only via ContractEditor mode=view */ }}
            title={isArabic ? 'المسودة' : 'Draft'}
            contractType={contractType}
            citations={citations}
            mode="view"
            showActions={true}
            onAction={onAction}
          />
        ) : (
          <div
            className="h-full overflow-auto px-6 py-5"
            style={{ background: '#FAFAFA' }}
            data-testid="draft-review-markdown"
          >
            {reviewText ? (
              <div className="prose prose-sm max-w-none" style={{ color: '#111' }}>
                <ReactMarkdown>{reviewText}</ReactMarkdown>
              </div>
            ) : (
              <div className="text-sm" style={{ color: '#777' }}>
                {isArabic
                  ? 'لا توجد مراجعة متاحة بعد.'
                  : 'No review available yet.'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DraftReviewTabs;
